import { translateActivityString } from './activityLocalization';

const asObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const cleanText = (value) => String(value ?? '').trim();

const firstValue = (before, after, ...keys) => {
  for (const source of [after, before]) {
    for (const key of keys) {
      const value = source[key];
      if (value !== null && value !== undefined && cleanText(value)) return value;
    }
  }
  return null;
};

const capitalize = (value) => {
  const text = cleanText(value);
  return text ? `${text.charAt(0).toLocaleUpperCase('ru-RU')}${text.slice(1)}` : '';
};

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const cleanSummary = (value, actorName = '') => {
  let text = translateActivityString(value)
    .replace(/\s*\[диспетчерский режим\]\s*/gi, ' (через диспетчера)')
    .replace(/\s*->\s*/g, ' → ')
    .trim();

  const actor = cleanText(actorName);
  if (actor) {
    text = text.replace(new RegExp(`^${escapeRegExp(actor)}(?:\\s+|:\\s*|[—-]\\s*)`, 'i'), '');
  }

  return capitalize(text.replace(/^[\s:;,.—-]+/, ''));
};

const quoted = (value) => {
  const text = translateActivityString(value);
  return text ? `«${text}»` : '';
};

const appendTarget = (prefix, target) => (
  cleanText(target) ? `${prefix} ${quoted(target)}` : prefix
);

const buildItemSubject = (before, after) => {
  const number = cleanText(firstValue(before, after, 'item_number'));
  const name = cleanText(firstValue(before, after, 'item_name'));
  if (number && name) return `№${number} — ${quoted(name)}`;
  if (number) return `№${number}`;
  if (name) return quoted(name);
  return '';
};

const buildTaskNumber = (log, before, after) => (
  cleanText(firstValue(before, after, 'task_number')) || cleanText(log?.entity_id)
);

const normalizeComparableText = (value) => cleanText(value)
  .toLocaleLowerCase('ru-RU')
  .replace(/[^a-zа-яё0-9]+/gi, ' ')
  .trim();

const isRedundantSummary = (summary, title, log) => {
  const normalized = normalizeComparableText(summary);
  if (!normalized) return true;

  const candidates = [
    title,
    log?.action_label,
    `${log?.action_label || ''}: ${log?.entity_label || ''}`,
  ].map(normalizeComparableText).filter(Boolean);

  return candidates.includes(normalized);
};

const TITLE_ONLY_ACTIONS = new Set([
  'login',
  'logout',
  'notification_read',
  'notifications_read_all',
  'news_read',
  'news_read_all',
  'order_comment_read',
  'task_comment_read',
  'order_item_added',
  'order_item_removed',
  'order_service_added',
  'order_service_removed',
  'order_discount_added',
  'order_discount_removed',
  'order_item_completed',
  'order_item_reopened',
  'order_item_storage_cell_updated',
  'order_stage_started',
  'order_stage_skipped',
  'order_stage_assigned',
  'order_stage_assignee_changed',
  'order_photo_added',
  'order_photo_removed',
  'order_invoice_prepared',
  'order_act_prepared',
  'order_documents_edited_post_completion',
  'order_invoice_resent',
  'task_assigned',
  'task_reassigned',
  'task_unassigned',
  'task_taken',
  'task_completed',
  'task_cancelled',
  'task_deferred',
  'task_deferred_resolved',
  'task_photo_added',
]);

const stripItemPrefix = (summary, before, after) => {
  const number = cleanText(firstValue(before, after, 'item_number'));
  const name = cleanText(firstValue(before, after, 'item_name'));
  let text = summary;

  for (const prefix of [
    number && name ? `${number} ${name}` : '',
    number,
    name,
  ].filter(Boolean)) {
    text = text.replace(new RegExp(`^${escapeRegExp(prefix)}\\s*:\\s*`, 'i'), '');
  }

  return capitalize(text);
};

const buildActionTitle = (log, before, after) => {
  const action = cleanText(log?.action).toLowerCase();
  const stage = firstValue(before, after, 'stage_label', 'stage', 'stage_name');
  const itemSubject = buildItemSubject(before, after);
  const namedTarget = firstValue(
    before,
    after,
    'title',
    'name',
    'company_name',
    'full_name',
    'login'
  ) || log?.target_name;

  const titles = {
    login: 'Вход в систему',
    logout: 'Выход из системы',
    ip_unblocked: 'IP-адрес разблокирован',
    force_logout_user: 'Пользователь принудительно выведен из системы',
    force_logout_all: 'Все пользователи принудительно выведены из системы',
    password_changed: 'Пароль изменён',
    password_reset_requested: 'Запрошен сброс пароля',
    password_reset_completed: 'Сброс пароля завершён',
    user_agreement_accepted: 'Принято пользовательское соглашение',
    create_user: appendTarget('Создан пользователь', namedTarget),
    update_user: appendTarget('Изменён пользователь', namedTarget),
    delete_user: appendTarget('Удалён пользователь', namedTarget),
    restore_user: appendTarget('Восстановлен пользователь', namedTarget),
    order_created: 'Создан заказ',
    order_deleted: 'Заказ удалён',
    order_cancelled: 'Заказ отменён',
    order_deferred: 'Задание отложено',
    order_deferred_resolved: 'Отложенное задание обработано',
    order_assignee_changed: cleanText(after.assignee_name)
      ? (cleanText(before.assignee_name) ? 'Исполнитель заказа изменён' : 'Заказ взят в работу')
      : 'Исполнитель снят с заказа',
    order_department_changed: 'Подразделение заказа изменено',
    order_status_changed: 'Статус заказа изменён',
    order_payment_changed: 'Параметры оплаты заказа изменены',
    order_pickup_updated: 'Условия забора заказа изменены',
    order_updated: 'Параметры заказа изменены',
    order_internal_notes_changed: 'Внутренняя заметка заказа изменена',
    order_dispatcher_call_required_stage_changed: 'Настройка созвона с клиентом изменена',
    order_emergency_dispatch_changed: 'Признак экстренного выезда изменён',
    order_dispatcher_priority_changed: 'Приоритет заказа изменён',
    order_item_added: itemSubject ? `Добавлен предмет ${itemSubject}` : 'Добавлен предмет в заказ',
    order_item_removed: itemSubject ? `Удалён предмет ${itemSubject}` : 'Удалён предмет из заказа',
    order_item_updated: itemSubject ? `Изменён предмет ${itemSubject}` : 'Изменён предмет заказа',
    order_item_completed: itemSubject ? `Предмет ${itemSubject} выполнен` : 'Предмет заказа выполнен',
    order_item_reopened: itemSubject ? `Предмет ${itemSubject} возвращён в работу` : 'Предмет возвращён в работу',
    order_item_storage_cell_updated: itemSubject
      ? `Для предмета ${itemSubject} изменена ячейка`
      : 'Для предмета заказа изменена ячейка',
    order_all_items_completed: stage
      ? `Все предметы этапа ${quoted(stage)} выполнены`
      : 'Все предметы этапа выполнены',
    order_service_added: appendTarget('Добавлена услуга', firstValue(before, after, 'service_name', 'item_name', 'name')),
    order_service_removed: appendTarget('Удалена услуга', firstValue(before, after, 'service_name', 'item_name', 'name')),
    order_discount_added: appendTarget('Добавлена скидка', firstValue(before, after, 'name', 'item_name', 'title')),
    order_discount_removed: appendTarget('Удалена скидка', firstValue(before, after, 'name', 'item_name', 'title')),
    order_stage_started: appendTarget('Начат этап', stage),
    order_stage_skipped: appendTarget('Пропущен этап', stage),
    order_stage_assigned: stage ? `Этап ${quoted(stage)} взят в работу` : 'Этап взят в работу',
    order_stage_assignee_changed: appendTarget('Изменён исполнитель этапа', stage),
    order_stage_completed: appendTarget('Завершён этап', stage),
    order_return_updated: 'Условия возврата заказа изменены',
    order_comment_added: 'Добавлен комментарий к заказу',
    order_comment_read: 'Прочитан комментарий к заказу',
    order_photo_added: 'Добавлено фото заказа',
    order_photo_removed: 'Удалено фото заказа',
    order_invoice_prepared: 'Подготовлен счёт по заказу',
    order_act_prepared: 'Подготовлен акт по заказу',
    order_invoice_sent: 'Счёт по заказу отправлен',
    order_invoice_resent: 'Счёт по заказу отправлен повторно',
    order_invoice_send_failed: 'Не удалось отправить счёт по заказу',
    order_invoice_downloaded: 'Скачан счёт по заказу',
    order_act_downloaded: 'Скачан акт по заказу',
    order_documents_package_downloaded: 'Скачан пакет документов по заказу',
    order_documents_edited_post_completion: 'Отредактированы документы закрытого заказа',
    order_work_sheet_exported: 'Сформирован рабочий лист заказов',
    order_route_sheet_exported: 'Сформирован путевой лист заказов',
    client_directory_exported: 'Выгружена клиентская база',
    order_revision_started: 'Открыта повторная обработка',
    order_revision_completed: 'Повторная обработка завершена',
    order_revision_cancelled: 'Повторная обработка отменена',
    order_revision_item_selected: itemSubject ? `Предмет ${itemSubject} добавлен в повтор` : 'Предмет добавлен в повтор',
    order_revision_item_unselected: itemSubject ? `Предмет ${itemSubject} убран из повтора` : 'Предмет убран из повтора',
    order_revision_pickup_updated: 'Условия забора повтора изменены',
    order_revision_return_updated: 'Условия возврата повтора изменены',
    order_rework_requested: 'Заказ отправлен на повторную обработку',
    order_rework_completed: 'Повторная обработка заказа завершена',
    client_intake_confirmed: 'Подтверждена клиентская заявка',
    client_intake_changes_requested: 'Запрошены изменения по заявке',
    client_intake_rejected: 'Отклонена клиентская заявка',
    client_chat_message_sent: 'Отправлено сообщение клиенту',
    task_created: 'Создано поручение',
    task_assigned: 'Назначен исполнитель поручения',
    task_reassigned: 'Исполнитель поручения изменён',
    task_unassigned: 'Исполнитель снят с поручения',
    task_taken: 'Поручение взято в работу',
    task_dispatch_updated: 'Поручение изменено',
    task_completed: 'Поручение выполнено',
    task_completed_without_photo_override: 'Поручение закрыто без фото',
    task_cancelled: 'Поручение отменено',
    task_deferred: 'Поручение отложено',
    task_deferred_resolved: 'Отложенное поручение обработано',
    task_comment_added: 'Добавлен комментарий к поручению',
    task_comment_read: 'Прочитан комментарий к поручению',
    task_photo_added: 'Добавлено фото к поручению',
    news_read: appendTarget('Прочитана новость', firstValue(before, after, 'title')),
    news_read_all: 'Прочитаны все новости',
    notification_created: appendTarget('Создано уведомление', firstValue(before, after, 'title')),
    notification_read: appendTarget('Прочитано уведомление', firstValue(before, after, 'title')),
    notifications_read_all: 'Прочитаны все уведомления',
    push_subscribed: 'Включены уведомления на устройстве',
    push_unsubscribed: 'Отключены уведомления на устройстве',
    push_subscriptions_cleared: 'Удалены старые подписки на уведомления',
  };

  if (titles[action]) return titles[action];

  if (['create', 'created'].includes(action)) {
    return appendTarget(`Создано: ${log?.entity_label || 'объект'}`, namedTarget);
  }
  if (['update', 'updated'].includes(action)) {
    return appendTarget(`Изменено: ${log?.entity_label || 'объект'}`, namedTarget);
  }
  if (['delete', 'deleted'].includes(action)) {
    return appendTarget(`Удалено: ${log?.entity_label || 'объект'}`, namedTarget);
  }

  return translateActivityString(log?.action_label) || 'Действие в системе';
};

const buildDescription = (log, before, after, title) => {
  const action = cleanText(log?.action).toLowerCase();
  const summary = cleanSummary(log?.summary, log?.user_name);

  if (action === 'order_created') {
    const snap = after.initial_snapshot;
    if (snap && Array.isArray(snap.items) && snap.items.length > 0) {
      return summary || `Состав при создании: ${snap.items.length} предм.`;
    }
    if (snap && Array.isArray(snap.items) && snap.items.length === 0) {
      return 'Создан без предметов (заявка на забор)';
    }
    return summary || '';
  }

  if (action === 'order_assignee_changed') {
    const previousName = cleanText(before.assignee_name);
    const nextName = cleanText(after.assignee_name);
    if (previousName || nextName) {
      return `Исполнитель: ${previousName || 'не назначен'} → ${nextName || 'не назначен'}`;
    }
  }

  if (action === 'order_stage_completed') {
    const previousStatus = translateActivityString(before.order_status);
    const nextStatus = translateActivityString(after.order_status);
    if (previousStatus || nextStatus) {
      return `Статус заказа: ${previousStatus || 'не указан'} → ${nextStatus || 'не указан'}`;
    }
  }

  if (action === 'order_all_items_completed' && after.completed_count !== undefined) {
    return `Отмечено предметов: ${after.completed_count}`;
  }

  if (action === 'order_item_updated' && summary) {
    return stripItemPrefix(summary, before, after);
  }

  if (['order_comment_added', 'task_comment_added'].includes(action) && summary) {
    return quoted(summary);
  }

  if (TITLE_ONLY_ACTIONS.has(action)) {
    return '';
  }

  return isRedundantSummary(summary, title, log) ? '' : summary;
};

const buildContext = (log, before, after) => {
  const orderNumber = cleanText(log?.order_number || firstValue(before, after, 'order_number'));
  const orderId = cleanText(log?.order_id || firstValue(before, after, 'order_id'));
  if (orderNumber || orderId) {
    return {
      kind: 'order',
      label: `Заказ №${orderNumber || orderId}`,
      href: orderId ? `/order/${orderId}` : '',
    };
  }

  if (cleanText(log?.entity_type) === 'task') {
    const taskNumber = buildTaskNumber(log, before, after);
    return {
      kind: 'task',
      label: taskNumber ? `Поручение №${taskNumber}` : 'Поручение',
      href: '',
    };
  }

  if (cleanText(log?.entity_type) === 'transaction') {
    return {
      kind: 'finance',
      label: log?.entity_id ? `Финансовая операция №${log.entity_id}` : 'Финансовая операция',
      href: '',
    };
  }

  return {
    kind: cleanText(log?.category) || 'system',
    label: translateActivityString(log?.entity_label || log?.category_label) || 'Система',
    href: '',
  };
};

export const getUserActivityPresentation = (log) => {
  const before = asObject(log?.old_value_data);
  const after = asObject(log?.new_value_data);
  const title = buildActionTitle(log, before, after);

  return {
    title,
    description: buildDescription(log, before, after, title),
    context: buildContext(log, before, after),
  };
};

const HIDDEN_ACTIVITY_FIELDS = new Set([
  'text',
  'target_user_id',
  'target_user_login',
  'target_login',
  'unassigned_orders',
  'unassigned_tasks',
  'dispatcher_access_mode',
  'initial_snapshot',
  'additional_service_ids',
  'additional_services_snapshot',
  'discount_snapshot',
  'item_ids',
  'action_by',
  'action_by_name',
  'completed_by',
  'completed_by_name',
  'completed_by_actor',
  'completed_by_actor_name',
  'completed_by_proxy',
  'approved_by_user_id',
  'rejected_by_user_id',
  'related_user_id',
  'transaction_id',
  'order_id',
  'order_number',
]);

export const isVisibleUserActivityField = (key) => {
  const normalized = cleanText(key).toLowerCase();
  if (!normalized || HIDDEN_ACTIVITY_FIELDS.has(normalized)) return false;
  if (/(?:^|_)(?:id|ids)$/.test(normalized)) return false;
  return !normalized.endsWith('_snapshot');
};

const FIELD_PRIORITY = [
  'order_status',
  'stage_status',
  'status',
  'assignee_name',
  'assigned_to_name',
  'stage_label',
  'stage',
  'item_number',
  'item_name',
  'quantity',
  'weight',
  'area',
  'return_delivery_type',
  'delivery_date',
  'delivery_time_slot',
  'delivery_address_text',
  'comment',
  'reason',
];

export const getUserActivityFieldPriority = (key) => {
  const index = FIELD_PRIORITY.indexOf(cleanText(key).toLowerCase());
  return index === -1 ? FIELD_PRIORITY.length : index;
};
